/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Button, Typography } from "@mui/material";
import { post } from "api/base";
import BaseInputMui from "components/base-input-mui";
import Spinner from "components/spinner";
import { EMAIL_REGEX, NOTIFICATION_TYPE } from "constants/common.constant";
import { ROUTERS } from "constants/routes.constant";
import { t } from "i18next";
import { TypeCommon } from "models/common";
import React, { ReactElement, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ToastMessage } from "utils/common.util";

const ConfirmEmailCodeView = (): ReactElement => {
  const navigate = useNavigate();
  const formCode = useForm<TypeCommon>({
    defaultValues: {
      codeValue: "",
      emailValue: "",
    },
  });
  const [searchParams] = useSearchParams();
  const id = searchParams.get("task_id");
  const request_mail_id = searchParams.get("request_mail_id");
  const autoSend = searchParams.get("auto_send") === "true";
  const emailFromParams = searchParams.get("email");
  // const email_access = searchParams.get("email_access");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowRefreshCode, setIsShowRefreshCode] = useState<boolean>(false);
  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);
  
  // If auto_send=true, set isEmailSent=true to show code input form immediately
  useEffect(() => {
    if (autoSend) {
      setIsEmailSent(true);
      if (emailFromParams) {
        formCode.setValue("emailValue", emailFromParams);
      }
    }
  }, [autoSend, emailFromParams]);
  
  const handleSendCode = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const emailToSend = formCode.getValues("emailValue");
      const conditionsSendEmail = {
        email_access: emailToSend,
        task_id: Number(id),
        request_mail_id: Number(request_mail_id),
        page_id: `${ROUTERS.CONFIRM_TEMPORARY_REQUEST_DETAIL}?task_id=${id}`,
      };
      const response = await post(
        "mail",
        "/mail/send-code",
        conditionsSendEmail,
      );
      if (response?.data) {
        setIsEmailSent(true);
        ToastMessage(NOTIFICATION_TYPE.SUCCESS, "担当者のメールアドレスへ認証コードを送信しました。届かない場合は管理者に連絡してください。");
      } else {
        ToastMessage(
          NOTIFICATION_TYPE.ERROR,
          response?.data?.message ?? "メールの送信に失敗しました",
        );
      }
    } catch (error: any) {
      if (error?.response?.status === 429) {
        setIsShowRefreshCode(true);
      }
      ToastMessage(
        NOTIFICATION_TYPE.ERROR,
        error?.response?.data?.message ?? t("alert-message.failure"),
      );
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleVerifyCode = async (): Promise<void> => {
    try {
      const codeToVerify = formCode.getValues("codeValue");
      if (codeToVerify && codeToVerify.length > 12) {
        formCode.setError("codeValue", {
          type: "manual",
          message: "認証コードは12文字以内で入力してください。",
        });
        return;
      }
      setIsLoading(true);
      const emailToVerify = formCode.getValues("emailValue");
      const conditionsSendEmail = {
        email_access: emailToVerify,
        code: formCode.getValues("codeValue"),
        task_id: id,
        request_mail_id: request_mail_id,
        page_id: `${ROUTERS.CONFIRM_TEMPORARY_REQUEST_DETAIL}?task_id=${id}`,
      };
      const response = await post(
        "mail",
        "/mail/verify-code",
        conditionsSendEmail,
      );
      if (response?.data) {
        navigate(
          `${ROUTERS.CONFIRM_TEMPORARY_REQUEST_DETAIL}?task_id=${id}&request_mail_id=${request_mail_id}`,
          {
            state: {
              token: response?.data?.access_token,
            },
          },
        );
      } else {
        ToastMessage(
          NOTIFICATION_TYPE.ERROR,
          response?.data?.message ?? "認証コードの形式が正しくありません。",
        );
      }
    } catch (error: any) {
      ToastMessage(
        NOTIFICATION_TYPE.ERROR,
        error?.response?.data?.message ?? t("alert-message.failure"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (isEmailSent) {
      handleVerifyCode();
    } else {
      formCode.handleSubmit(handleSendCode)();
    }
  };

  return (
    <>
      {isLoading && <Spinner isFullWidth={true} />}
      <Box className="flex flex-col gap-5 shadow-md border border-solid border-[#ccc] bg-white rounded-lg px-5 py-10 w-full sm:w-[60%] md:w-[50%] lg:w-[30%] xl:w-[25%]">
        <Box
          component={"form"}
          autoComplete="off"
          className="flex flex-col gap-3 my-5"
          onSubmit={handleFormSubmit}
        >
          {!isEmailSent && (
            <>
              <span className="text-[12px] md:text-[14px] block w-full whitespace-normal break-words">
                認証コードを受信するメールアドレスを入力してください。
              </span>
              <Typography variant="body2" component="span" fontWeight="bold">
                メールアドレス
                <span className="required-field">* </span>
              </Typography>
              <Controller
                name="emailValue"
                control={formCode.control}
                rules={{
                  required: {
                    value: true,
                    message: "メールアドレスを入力してください。",
                  },
                  pattern: {
                    value: EMAIL_REGEX,
                    message: "有効なメールアドレスを入力してください。",
                  },
                }}
                render={({ field }) => (
                  <BaseInputMui
                    {...field}
                    placeholder="メールアドレスを入力"
                    fullWidth
                    required
                    margin="0"
                    className="custom-input-field"
                    error={!!formCode.formState.errors["emailValue"]}
                    helperText={
                      formCode.formState.errors["emailValue"]?.message as string
                    }
                    type="email"
                  />
                )}
              />
            </>
          )}
          
          {isEmailSent && (
            <>
              <span className="text-[12px] md:text-[14px] block w-full whitespace-normal break-words">
                入力したメールアドレスに届いた認証コードを入力してください。
              </span>
              <span className="text-[12px] md:text-[14px] !mt-[-8px] block w-full whitespace-normal break-words">
                発行されていない場合は認証取得ボタンをクリックしてください。
              </span>
              <span className="text-[12px] md:text-[14px] !mt-[-8px] block w-full whitespace-normal break-words">
                ※コードが届くまでに、1分ぐらいかかる場合があります。
              </span>
              <Typography variant="body2" component="span" fontWeight="bold">
                認証コード入力
                <span className="required-field">* </span>
              </Typography>
              <Controller
                name="codeValue"
                control={formCode.control}
                rules={{
                  required: {
                    value: true,
                    message: "認証コードを入力してください。",
                  },
                }}
                render={({ field }) => (
                  <BaseInputMui
                    {...field}
                    placeholder="認証コード入力を入力"
                    fullWidth
                    required
                    margin="0"
                    className="custom-input-field"
                    error={!!formCode.formState.errors["codeValue"]}
                    helperText={
                      formCode.formState.errors["codeValue"]?.message as string
                    }
                    inputProps={{
                      maxLength: isShowRefreshCode ? 15 : 12,
                    }}
                  />
                )}
              />
            </>
          )}
        </Box>
        
        {isEmailSent && (
          <Button
            size="large"
            onClick={formCode.handleSubmit(() => {
              handleVerifyCode();
            })}
            fullWidth
            variant="contained"
            className="!h-[50px]"
          >
            確定
          </Button>
        )}
        
        <Button
          size="large"
          onClick={() => {
            if (isEmailSent) {
              handleSendCode();
            } else {
              formCode.handleSubmit(handleSendCode)();
            }
          }}
          fullWidth
          variant="outlined"
          className="!mb-3 !h-[50px]"
        >
          認証コード取得
        </Button>
        {/* {isShowRefreshCode && (
        <Button
          size="large"
          onClick={() => {
            formCode.handleSubmit(handleRefreshCode)();
          }}
          fullWidth
          variant="outlined"
          className="!mb-5 !h-[50px]"
        >
          認証コード再取得
        </Button>
        )} */}
      </Box>
    </>
  );
};

export default ConfirmEmailCodeView;
